import { isEqual } from 'lodash-es';

export interface IParams {
  [key: string]: any;
}

export const isEmpty = (value: any) =>
  value === '' ||
  value === undefined ||
  value === null ||
  (Array.isArray(value) && !value.length);

// 过滤为空的参数
export const filterParams = (params: any = {}) => {
  const data: IParams = {};

  Object.keys(params).forEach((key) => {
    const value = params[key];

    if (!isEmpty(value)) {
      data[key] = value;
    }
  });

  return data;
};

export const isPlainObject = (data: any) =>
  Object.prototype.toString.call(data) === '[object Object]';

export const querystring = (params: IParams = {}, prefix = true) => {
  const qs: string[] = [];

  Object.keys(params).forEach((key) => {
    const value = params[key];

    if (!isEmpty(value)) {
      qs.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  if (!qs.length) {
    return '';
  }

  return prefix ? `?${qs.join('&')}` : qs.join('&');
};

export const httpErrorToMessage: {
  zh: Record<string, string>;
  en: Record<string, string>;
  es: Record<string, string>;
  id: Record<string, string>;
  pt: Record<string, string>;
} = {
  zh: {
    0: '网络抖动，若系统无响应，请检查网络并刷新页面',
    401: '网络抖动，若系统无响应，请检查网络并刷新页面',
    404: '系统异常，若影响使用，请提交反馈',
    405: '系统异常，若影响使用，请提交反馈',
    '4xx': '网络抖动，若系统无响应，请检查网络并刷新页面',
    '500': '系统异常，若影响使用，请提交反馈',
    '502': '网络抖动，若影响使用，请重试当前操作',
    '504': '网络抖动，若影响使用，请重试当前操作',
    '5xx': '网络抖动导致操作失败，请重试当前操作',
    SyntaxError: '系统异常，若影响使用，请提交反馈',
  },
  en: {
    0: 'Network jitter, if the system is not responding, please check the network and refresh the page',
    401: 'Network jitter, if the system is not responding, please check the network and refresh the page',
    404: 'System exception, if it affects the use, please submit feedback',
    405: 'System exception, if it affects the use, please submit feedback',
    '4xx':
      'Network jitter, if the system is not responding, please check the network and refresh the page',
    '500': 'System exception, if it affects the use, please submit feedback',
    '502':
      'Network jitter, if it affects usage, please retry the current operation',
    '504':
      'Network jitter, if it affects usage, please retry the current operation',
    '5xx':
      'The operation failed due to network jitter, please retry the current operation',
    SyntaxError: '系统异常，若影响使用，请提交反馈',
  },
  es: {
    0: 'Jitter de red, si el sistema no responde, verifique la red y actualice la página',
    401: 'Jitter de red, si el sistema no responde, verifique la red y actualice la página',
    404: 'El sistema es anormal. Si afecta al uso, envíe sus comentarios.',
    405: 'El sistema es anormal. Si afecta al uso, envíe sus comentarios.',
    '4xx':
      'Jitter de red, si el sistema no responde, verifique la red y actualice la página',
    '500': 'El sistema es anormal. Si afecta al uso, envíe sus comentarios.',
    '502':
      'Jitter de red, si afecta el uso, vuelva a intentar la operación actual',
    '504':
      'Jitter de red, si afecta el uso, vuelva a intentar la operación actual',
    '5xx':
      'La fluctuación de la red provocó que la operación fallara, vuelva a intentar la operación actual',
    SyntaxError:
      'El sistema es anormal. Si afecta al uso, envíe sus comentarios.',
  },
  id: {
    0: 'Kegelisahan jaringan, jika sistem tidak merespons, silakan periksa jaringan dan segarkan halaman',
    401: 'Kegelisahan jaringan, jika sistem tidak merespons, silakan periksa jaringan dan segarkan halaman',
    404: 'Sistemnya tidak normal. Jika itu mempengaruhi penggunaan, harap kirimkan umpan balik.',
    405: 'Sistemnya tidak normal. Jika itu mempengaruhi penggunaan, harap kirimkan umpan balik.',
    '4xx':
      'Kegelisahan jaringan, jika sistem tidak merespons, silakan periksa jaringan dan segarkan halaman',
    '500':
      'Sistemnya tidak normal. Jika itu mempengaruhi penggunaan, harap kirimkan umpan balik.',
    '502':
      'Jaringan jitter, jika mempengaruhi penggunaan, silakan coba lagi operasi saat ini',
    '504':
      'Jaringan jitter, jika mempengaruhi penggunaan, silakan coba lagi operasi saat ini',
    '5xx':
      'Kegelisahan jaringan menyebabkan operasi gagal, silakan coba lagi operasi saat ini',
    SyntaxError:
      'Sistemnya tidak normal. Jika itu mempengaruhi penggunaan, harap kirimkan umpan balik.',
  },
  pt: {
    0: 'Jitter de rede, se o sistema não responder, verifique a rede e atualize a página',
    401: 'Jitter de rede, se o sistema não responder, verifique a rede e atualize a página',
    404: 'O sistema é anormal. Se isso afetar o uso, envie feedback.',
    405: 'O sistema é anormal. Se isso afetar o uso, envie feedback.',
    '4xx':
      'Jitter de rede, se o sistema não responder, verifique a rede e atualize a página',
    '500': 'O sistema é anormal. Se isso afetar o uso, envie feedback.',
    '502': 'Jitter de rede, se afetar o uso, tente novamente a operação atual',
    '504': 'Jitter de rede, se afetar o uso, tente novamente a operação atual',
    '5xx':
      'Jitter de rede causou a falha da operação, tente novamente a operação atual',
    SyntaxError: 'O sistema é anormal. Se isso afetar o uso, envie feedback.',
  },
};

// 驼峰化
export const camelifyString = (str: string) =>
  str
    .replace(/^([A-Z])/, (_, s) => s.toLowerCase())
    .replace(/_(\S)/g, (_, s) => s.toUpperCase());

export const camelify = (data: any, dealString = false) => {
  if (typeof data === 'string') {
    return dealString ? camelifyString(data) : data;
  }

  if (data instanceof Date || data === null) {
    return data;
  }

  let result: any = {};

  if (Array.isArray(data)) {
    result = data.map((item: any) => camelify(item, dealString));
  } else if (typeof data === 'object') {
    Object.keys(data).forEach((key: any) => {
      const value = data[key];
      const camelifiedKey = camelifyString(key);

      result[camelifiedKey] =
        typeof value === 'object' ? camelify(value, dealString) : value;
    });
  } else {
    result = data;
  }

  return result;
};

interface ICache<P> {
  /** 缓存值 */
  value: P;
  /** 缓存时间 */
  timestamp: number;
}

/** 类似 memoize, 带过期时间 */
export const memoizeFn = <T extends Array<any>, P>(
  /** 被缓存的函数 */
  fn: (...args: T) => P,
  /** 缓存过期时间， 默认 1 分钟, 传0则代表不过期 */
  timeout: number = 60000,
  /* 最多缓存结果个数，避免内存溢出 */
  maxCount: number = 10,
) => {
  // 每一次调用都形成一个闭包，缓存在闭包内
  const caches = new Map<T, ICache<P>>();
  return (...args: T) => {
    // 当次调用的缓存
    let cache: ICache<P> | undefined;
    let matchKey: T | undefined;
    // 从闭包中执行命中缓存逻辑
    for (const key of caches.keys()) {
      /** 所有参数进行一次深比较 */
      if (isEqual(args, key)) {
        cache = caches.get(key);
        matchKey = key;
        break;
      }
    }
    if (cache && matchKey) {
      // 若命中缓存再查看是否过期
      const now = Date.now();
      if (timeout && now - cache.timestamp > timeout) {
        cache = undefined;
        caches.delete(matchKey);
      }
    }
    if (!cache) {
      cache = {
        value: fn(...args),
        timestamp: Date.now(),
      };
      if (cache.value instanceof Promise) {
        // 异步出错不缓存
        cache.value.catch(() => {
          caches.delete(args);
        });
      }
      /* 缓存数量达到最大，则清除最早执行的结果 */
      if (caches.size >= maxCount) {
        let removeKey;
        let firstTime;
        for (const key of caches.keys()) {
          const { timestamp } = caches.get(key)!;
          if (!firstTime || timestamp < firstTime) {
            firstTime = timestamp;
            removeKey = key;
          }
        }
        if (removeKey) {
          caches.delete(removeKey);
        }
      }
      // 记录缓存
      caches.set(args, cache);
    }

    return cache.value;
  };
};
